import {
  billedSeats,
  planLabel,
  planLimitMessage,
  PLANS,
  PRICES,
  TEAM_MIN_EDITORS,
  type BillingInterval,
  type MeResponse,
  type PlanId,
  type WorkspaceSummary,
} from '@nolens/domain';
import { formatDate, formatNumber } from '@nolens/i18n';
import {
  Banner,
  Button,
  Dialog,
  Field,
  Input,
  ProgressBar,
  SegmentedControl,
  Switch,
  useLocale,
  useToast,
} from '@nolens/ui';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import type { Crumb } from '../components/AppShell';
import {
  BrandIcon,
  CardIcon,
  ChevronRightIcon,
  DownloadIcon,
  ExtensionIcon,
  LockIcon,
  MembersIcon,
  SlidersIcon,
  UserIcon,
} from '../components/Icons';
import { ProTag } from '../components/PlanLimit';
import { QueryView } from '../components/Query';
import { SpaceTile } from '../components/SpaceTile';
import { ApiError } from '../lib/api';
import { spaceName } from '../lib/locale';
import {
  useBillingActions,
  useMe,
  useMembers,
  useWorkspaceActions,
  useWorkspaceBilling,
  useWorkspacePlan,
} from '../lib/queries';
import { openCheckout } from '../lib/paddle';
import { canExportSpace, exportSpaceZip, SPACE_EXPORT_MAX, type SpaceExport } from '../lib/exports';
import { CONTACT_EMAIL, mailto } from '../lib/contact';
import { membersText } from '../i18n/members';
import { eur, publicPaths } from '../i18n/landing';
import { settingsText } from '../i18n/settings';
import { shellText } from '../i18n/shell';
import { spaceCrumbs, WithMe } from './workspace';

/**
 * Página de um espaço dentro das definições: barra lateral das definições e caminho
 * «Espaço / Definições / …». Sem o espaço (ou sem acesso), diz porquê.
 */
function SettingsFrame({
  page,
  teamOnly,
  children,
}: {
  page?: string;
  teamOnly?: boolean;
  children: (ws: WorkspaceSummary, me: MeResponse) => ReactNode;
}) {
  const { workspaceId } = useParams();
  const locale = useLocale();
  const t = settingsText(locale);
  const st = shellText(locale);
  const crumbs = (me: MeResponse) => {
    const rest: Crumb[] = page
      ? [{ label: st.settings, to: `/w/${workspaceId}/definicoes` }, { label: page }]
      : [{ label: st.settings }];
    return spaceCrumbs(me, workspaceId, locale, ...rest);
  };
  return (
    <WithMe current={workspaceId} settings crumbs={crumbs}>
      {(me) => {
        const ws = me.workspaces.find((w) => w.id === workspaceId);
        if (!ws) return <p className="text-text-2">{t.spaceNotFound}</p>;
        if (teamOnly && ws.kind !== 'team') return <p className="text-text-2">{t.onlyTeams}</p>;
        return children(ws, me);
      }}
    </WithMe>
  );
}

function SpaceHeader({ ws, title }: { ws: WorkspaceSummary; title?: string }) {
  const locale = useLocale();
  const t = settingsText(locale);
  return (
    <div className="flex items-center gap-4">
      <SpaceTile ws={ws} size={44} />
      <div className="min-w-0">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-text-3">
          {title ? spaceName(ws, locale) : ws.kind === 'team' ? t.team : t.personalSpace}
        </p>
        <h1 className="truncate text-2xl font-semibold tracking-tight">
          {title ?? spaceName(ws, locale)}
        </h1>
      </div>
    </div>
  );
}

function SettingsCard({
  to,
  icon,
  title,
  hint,
  tag,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  hint: string;
  tag?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 rounded-[16px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)] transition hover:shadow-[inset_0_0_0_1px_var(--border-strong)]"
    >
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-surface-2 text-text-2 transition group-hover:bg-accent-soft group-hover:text-accent-text">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center font-medium">
          {title}
          {tag}
        </span>
        <span className="mt-0.5 block text-[13.5px] text-text-2">{hint}</span>
      </span>
      <ChevronRightIcon size={16} className="shrink-0 text-text-3" />
    </Link>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-text-3">
        {title}
      </h2>
      <div className="grid gap-3 lg:grid-cols-2">{children}</div>
    </section>
  );
}

/** Definições de um espaço: cartões agrupados para cada página. */
export function SettingsPage() {
  const locale = useLocale();
  const t = settingsText(locale);
  return (
    <SettingsFrame>
      {(ws, me) => {
        const base = `/w/${ws.id}`;
        const team = ws.kind === 'team';
        const owner = ws.role === 'owner';
        return (
          <div className="grid max-w-4xl gap-8" data-settings>
            <SpaceHeader ws={ws} />
            {(team || owner) && (
              <Group title={t.groupSpace}>
                {team && (
                  <SettingsCard
                    to={`${base}/definicoes/geral`}
                    icon={<SlidersIcon />}
                    title={t.general}
                    hint={t.generalHint}
                  />
                )}
                {owner && canExportSpace(me) && (
                  <SettingsCard
                    to={`${base}/definicoes/exportar`}
                    icon={<DownloadIcon />}
                    title={t.exportGuides}
                    hint={t.exportHint}
                  />
                )}
                {owner && (
                  <SettingsCard
                    to={`${base}/marca`}
                    icon={<BrandIcon />}
                    title={t.brand}
                    hint={t.brandHint}
                    tag={PLANS[ws.plan].brand ? undefined : <ProTag />}
                  />
                )}
              </Group>
            )}
            {team && (
              <Group title={t.groupPeople}>
                <SettingsCard
                  to={`${base}/membros`}
                  icon={<MembersIcon />}
                  title={t.members}
                  hint={t.membersHint}
                />
                <SettingsCard
                  to={`${base}/definicoes/gravacoes`}
                  icon={<LockIcon />}
                  title={t.recordings}
                  hint={t.recordingsHint}
                />
              </Group>
            )}
            <Group title={t.groupPlan}>
              <SettingsCard
                to={`${base}/definicoes/plano`}
                icon={<CardIcon />}
                title={t.plan}
                hint={t.planHint}
              />
            </Group>
            <Group title={t.groupAccount}>
              <SettingsCard
                to="/conta"
                icon={<UserIcon />}
                title={t.account}
                hint={t.accountHint}
              />
              <SettingsCard
                to="/extensao"
                icon={<ExtensionIcon />}
                title={t.extension}
                hint={t.extensionHint}
              />
            </Group>
          </div>
        );
      }}
    </SettingsFrame>
  );
}

/**
 * Exportar todos os guias do espaço num ZIP (só o dono). Escondida enquanto o serviço não
 * fechar: só o proprietário da aplicação a vê (canExportSpace).
 */
export function ExportSettingsPage() {
  const t = settingsText(useLocale());
  return (
    <SettingsFrame page={t.exportGuides}>
      {(ws, me) =>
        canExportSpace(me) ? (
          <ExportGuides key={ws.id} ws={ws} />
        ) : (
          <Navigate to={`/w/${ws.id}/definicoes`} replace />
        )
      }
    </SettingsFrame>
  );
}

function ExportGuides({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = settingsText(locale);
  const [progress, setProgress] = useState<[number, number] | null>(null);
  const [result, setResult] = useState<SpaceExport | null>(null);
  const [failed, setFailed] = useState(false);
  const run = async () => {
    setFailed(false);
    setResult(null);
    setProgress([0, 0]);
    try {
      setResult(
        await exportSpaceZip({ id: ws.id, name: spaceName(ws, locale) }, locale, (d, n) =>
          setProgress([d, n]),
        ),
      );
    } catch {
      setFailed(true);
    } finally {
      setProgress(null);
    }
  };
  return (
    <div className="grid max-w-xl gap-6">
      <SpaceHeader ws={ws} title={t.exportGuides} />
      <section
        className="grid gap-4 rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
        data-export-space
      >
        <p className="text-sm text-text-2">{t.exportIntro}</p>
        <p className="text-[13px] text-text-3">{t.exportNote(SPACE_EXPORT_MAX)}</p>
        {ws.role !== 'owner' ? (
          <p className="text-sm text-text-2">{t.exportOnlyOwner}</p>
        ) : (
          <Button
            className="justify-self-start"
            loading={progress !== null}
            onClick={run}
            data-export-run
          >
            {progress && progress[1] ? t.exportProgress(progress[0], progress[1]) : t.exportButton}
          </Button>
        )}
        {failed && <Banner tone="critical">{t.exportFailed}</Banner>}
        {result && (
          <Banner tone={result.missing ? 'attention' : 'positive'}>
            {result.guides ? t.exportDone(result.guides, result.parts) : t.exportEmpty}
            {result.missing ? ` ${t.exportMissing(result.missing)}` : ''}
          </Banner>
        )}
      </section>
    </div>
  );
}

/** Geral (equipas): nome da equipa (só o dono), sair e apagar. */
export function GeneralSettingsPage() {
  const t = settingsText(useLocale());
  return (
    <SettingsFrame page={t.general} teamOnly>
      {(ws) => <General key={ws.id} ws={ws} />}
    </SettingsFrame>
  );
}

function General({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = settingsText(locale);
  const m = membersText(locale);
  const owner = ws.role === 'owner';
  const act = useWorkspaceActions(ws.id);
  const members = useMembers(ws.id);
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState(ws.name);
  const [confirm, setConfirm] = useState<'leave' | 'delete' | null>(null);
  const error = [act.rename, act.remove, act.removeMember]
    .map((x) => x.error)
    .find((e) => e instanceof ApiError) as ApiError | undefined;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    act.rename.mutate(name.trim(), { onSuccess: () => toast(t.saved, 'positive') });
  };
  return (
    <div className="grid max-w-xl gap-8">
      <SpaceHeader ws={ws} title={t.general} />
      {error && (
        <Banner tone="critical">
          {error.code === 'VALIDATION_FAILED' && confirm === 'leave' ? m.needsOwner : error.message}
        </Banner>
      )}
      <form
        onSubmit={submit}
        className="grid gap-4 rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
      >
        <Field label={t.teamName} hint={owner ? undefined : t.onlyOwnerRenames}>
          {(p) => (
            <Input
              {...p}
              required
              maxLength={80}
              disabled={!owner}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        {owner && (
          <Button
            type="submit"
            size="sm"
            className="justify-self-start"
            loading={act.rename.isPending}
            disabled={!name.trim() || name.trim() === ws.name}
          >
            {t.save}
          </Button>
        )}
      </form>
      <section>
        <h2 className="font-semibold">{t.leaveOrDelete}</h2>
        <p className="mt-1 text-sm text-text-2">{t.leaveHint}</p>
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setConfirm('leave')}>
            {m.leaveTeam}
          </Button>
          {owner && (
            <Button size="sm" variant="danger" onClick={() => setConfirm('delete')}>
              {m.deleteTeam}
            </Button>
          )}
        </div>
      </section>
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'delete' ? m.deleteTitle : m.leaveTitle}
        description={confirm === 'delete' ? m.deleteHint : m.leaveHint}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              {m.cancel}
            </Button>
            <Button
              variant="danger"
              loading={act.remove.isPending || act.removeMember.isPending}
              onClick={() => {
                const done = {
                  onSuccess: () => navigate('/inicio'),
                  onSettled: () => setConfirm(null),
                };
                if (confirm === 'delete') act.remove.mutate(undefined, done);
                else {
                  const meId = members.data?.find((x) => x.you)?.userId;
                  if (meId) act.removeMember.mutate(meId, done);
                }
              }}
            >
              {confirm === 'delete' ? m.delete : m.leave}
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
    </div>
  );
}

/** Gravações (equipas): rever antes de enviar e guardar o texto escrito (decide o dono). */
export function RecordingSettingsPage() {
  const t = settingsText(useLocale());
  return (
    <SettingsFrame page={t.recordings} teamOnly>
      {(ws) => <Recordings ws={ws} />}
    </SettingsFrame>
  );
}

function Recordings({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = settingsText(locale);
  const m = membersText(locale);
  const owner = ws.role === 'owner';
  const act = useWorkspaceActions(ws.id);
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="grid max-w-2xl gap-6">
      <SpaceHeader ws={ws} title={t.recordings} />
      {act.policies.error instanceof ApiError && (
        <Banner tone="critical">{act.policies.error.message}</Banner>
      )}
      <div data-recording-policies>
        <h2 className="font-semibold">{m.privacyTitle}</h2>
        <p className="mt-1 text-sm text-text-2">{m.privacyHint}</p>
        {owner ? (
          <div className="mt-4 flex flex-col gap-4 rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]">
            <Switch
              checked={ws.reviewRequired}
              onChange={(v) => act.policies.mutate({ reviewRequired: v })}
              label={m.reviewRequired}
              description={m.reviewRequiredHint}
            />
            <Switch
              checked={ws.keepTyped}
              onChange={(v) => (v ? act.policies.mutate({ keepTyped: true }) : setConfirm(true))}
              label={m.keepTyped}
              description={m.keepTypedHint}
            />
          </div>
        ) : (
          <ul className="mt-3 flex flex-col gap-1 text-sm text-text-2">
            <li>{ws.reviewRequired ? m.teamReviews : t.reviewOptional}</li>
            <li>{ws.keepTyped ? t.keepsTyped : m.teamNoTyped}</li>
          </ul>
        )}
      </div>
      <Dialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title={m.dropTypedTitle}
        description={m.dropTypedHint}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(false)}>
              {m.cancel}
            </Button>
            <Button
              variant="danger"
              loading={act.policies.isPending}
              onClick={() =>
                act.policies.mutate({ keepTyped: false }, { onSettled: () => setConfirm(false) })
              }
            >
              {m.dropTyped}
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
    </div>
  );
}

/** Plano e utilização: o plano do espaço, guias e espaço das capturas, e os planos. */
export function PlanSettingsPage() {
  const t = settingsText(useLocale());
  return <SettingsFrame page={t.plan}>{(ws) => <PlanUsage ws={ws} />}</SettingsFrame>;
}

function PlanUsage({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = settingsText(locale);
  const q = useWorkspacePlan(ws.id);
  return (
    <div className="grid max-w-2xl gap-6">
      <SpaceHeader ws={ws} title={t.planTitle} />
      <p className="-mt-2 text-sm text-text-2">{t.planIntro}</p>
      <CurrentPlan ws={ws} />
      <Subscription ws={ws} />
      <QueryView q={q}>
        {(p) => (
          <div className="grid gap-5 rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]">
            <div>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium">{t.guides}</span>
                <span className="text-text-2">
                  {p.features.guides === null
                    ? `${formatNumber(locale, p.usage.guides)} · ${t.unlimited}`
                    : `${p.usage.guides} / ${p.features.guides}`}
                </span>
              </div>
              {p.features.guides !== null && (
                <ProgressBar
                  className="mt-2"
                  label={t.guides}
                  value={p.usage.guides}
                  max={p.features.guides}
                />
              )}
            </div>
            <div>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium">{t.screenshots}</span>
                <span className="text-text-2">
                  {t.storageOf(
                    formatNumber(locale, p.usage.storageMb),
                    formatNumber(locale, p.storageLimitMb / 1024),
                  )}
                </span>
              </div>
              <ProgressBar
                className="mt-2"
                label={t.screenshots}
                value={p.usage.storageMb}
                max={p.storageLimitMb}
              />
            </div>
            <Link to={publicPaths(locale).pricing} className="justify-self-start">
              <Button size="sm" variant="secondary">
                {t.seePlans}
              </Button>
            </Link>
          </div>
        )}
      </QueryView>
    </div>
  );
}

/** Nome e estado do plano de um espaço (também na página «Conta»). */
export function usePlanState(
  ws: WorkspaceSummary,
): { label: string; state: string; plan: PlanId } | null {
  const locale = useLocale();
  const t = settingsText(locale);
  const p = useWorkspacePlan(ws.id).data;
  const b = useWorkspaceBilling(ws.id).data;
  if (!p) return null;
  const date = (d: string | null) => (d ? formatDate(locale, d) : '');
  const state =
    p.plan === 'free'
      ? t.stateFree
      : p.plan === 'expired'
        ? planLimitMessage('expired', locale)
        : b?.subscribed
          ? (b.status === 'past_due'
              ? t.pastDue
              : b.status === 'canceled'
                ? t.canceledUntil(date(b.currentPeriodEnd))
                : t.activeUntil(date(b.currentPeriodEnd))) +
            (p.plan === 'team' && b.seats ? ` ${t.seatsBilled(b.seats)}` : '')
          : p.plan === 'team' && p.status === 'trialing' && p.trialEndsAt
            ? t.stateTrial(date(p.trialEndsAt))
            : t.stateGranted(p.status === 'trialing' ? date(p.trialEndsAt) || null : null);
  return { label: planLabel(p.plan, locale), state, plan: p.plan };
}

/**
 * O plano em vigor, em destaque: nome, estado (subscrição, teste, ativado sem subscrição ou só
 * leitura) e a que espaço se aplica (o pessoal tem o Pro; cada equipa tem o seu Pro Equipa).
 */
function CurrentPlan({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = settingsText(locale);
  const s = usePlanState(ws);
  if (!s) return null;
  return (
    <section
      className="grid gap-1.5 rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
      data-current-plan={s.plan}
    >
      <p className="text-[12.5px] font-medium uppercase tracking-wide text-text-3">
        {t.currentPlan}
      </p>
      <p className="text-2xl font-semibold tracking-tight">{s.label}</p>
      <p className={s.plan === 'expired' ? 'text-sm text-attention' : 'text-sm text-text-2'}>
        {s.state}
      </p>
      <p className="text-[13px] text-text-3">
        {ws.kind === 'team' ? t.scopeTeam(spaceName(ws, locale)) : t.scopePersonal}
      </p>
    </section>
  );
}

/**
 * Subscrição paga (Paddle): checkout para quem ainda não tem, estado e portal do cliente para
 * quem tem. Sem pagamentos ligados na API, não aparece.
 */
function Subscription({ ws }: { ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = settingsText(locale);
  const q = useWorkspaceBilling(ws.id);
  const plan = useWorkspacePlan(ws.id);
  const { checkout, portal } = useBillingActions(ws.id);
  const qc = useQueryClient();
  const [cycle, setCycle] = useState<BillingInterval>('yearly');
  const [paid, setPaid] = useState(false);
  const [failed, setFailed] = useState(false);
  const b = q.data;

  // Depois de pagar, o webhook ativa o plano em segundos: pergunta até lá (no máximo 1 minuto).
  useEffect(() => {
    if (!paid || b?.subscribed) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      void qc.invalidateQueries({ queryKey: ['billing', ws.id] });
      void qc.invalidateQueries({ queryKey: ['plan', ws.id] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      if (Date.now() - started > 60_000) window.clearInterval(id);
    }, 2000);
    return () => window.clearInterval(id);
  }, [paid, b?.subscribed, qc, ws.id]);

  if (!b) return null;
  // Pagamentos ainda não ligados: o plano pede-se por email
  if (!b.available)
    return b.subscribed || !b.canManage ? null : (
      <p className="text-sm text-text-2">
        {t.paymentsSoon}{' '}
        <a
          className="underline underline-offset-2"
          href={mailto(t.planSubject(planLabel(b.product, locale)))}
        >
          {CONTACT_EMAIL}
        </a>
      </p>
    );
  const product = b.product;
  const prices = PRICES[product];
  const seats = billedSeats(plan.data?.usage.editors ?? 1);
  const unit = cycle === 'yearly' ? prices.yearly : prices.monthly;
  const per = cycle === 'yearly' ? t.perYear : t.perMonth;
  const date = (d: string | null) => (d ? formatDate(locale, d) : '');

  const buy = async () => {
    setFailed(false);
    try {
      const session = await checkout.mutateAsync(cycle);
      await openCheckout(session, locale, {
        completed: () => setPaid(true),
        closed: () => undefined,
      });
    } catch {
      setFailed(true);
    }
  };

  const manage = async () => {
    setFailed(false);
    try {
      window.location.assign((await portal.mutateAsync()).url);
    } catch {
      setFailed(true);
    }
  };

  return (
    <section
      className="grid gap-4 rounded-[16px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
      data-billing-section
    >
      <h2 className="font-semibold">{t.subscription}</h2>
      {paid && !b.subscribed && <Banner tone="positive">{t.paid}</Banner>}
      {failed && <Banner tone="critical">{t.checkoutFailed}</Banner>}
      {b.subscribed ? (
        <>
          <p className="text-sm text-text-2">
            {b.status === 'past_due'
              ? t.pastDue
              : b.status === 'canceled'
                ? t.canceledUntil(date(b.currentPeriodEnd))
                : t.activeUntil(date(b.currentPeriodEnd))}
            {product === 'team' && b.seats ? ` ${t.seatsBilled(b.seats)}` : ''}
          </p>
          {b.canManage && (
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" variant="secondary" loading={portal.isPending} onClick={manage}>
                {t.manage}
              </Button>
              <span className="text-[13px] text-text-3">{t.manageHint}</span>
            </div>
          )}
        </>
      ) : !b.canManage ? (
        <p className="text-sm text-text-2">{t.onlyOwnerBuys}</p>
      ) : (
        <>
          {/* Plano ativo sem subscrição (dado à mão ou em teste): diz porque se subscreve */}
          {plan.data && plan.data.plan === product && (
            <p className="text-sm text-text-2" data-not-subscribed>
              {t.notSubscribedYet(
                planLabel(product, locale),
                plan.data.trialEndsAt ? date(plan.data.trialEndsAt) : null,
              )}
            </p>
          )}
          <div className="justify-self-start">
            <SegmentedControl
              value={cycle}
              onChange={setCycle}
              label={t.billingInterval}
              options={[
                { value: 'yearly', label: t.yearly },
                { value: 'monthly', label: t.monthly },
              ]}
            />
          </div>
          <p className="text-sm">
            <span className="text-lg font-semibold tabular-nums">
              {product === 'team'
                ? t.teamTotal(seats, per(eur(locale, unit)))
                : per(eur(locale, unit))}
            </span>
          </p>
          {product === 'team' && (
            <p className="-mt-2 text-[13px] text-text-3">{t.teamSeats(seats, TEAM_MIN_EDITORS)}</p>
          )}
          <Button
            className="justify-self-start"
            loading={checkout.isPending}
            onClick={buy}
            data-subscribe
          >
            {product === 'team' ? t.subscribeTeam : t.subscribePro}
          </Button>
        </>
      )}
      <p className="text-[12.5px] text-text-3">
        {t.paddleNote}{' '}
        <Link to={publicPaths(locale).refunds} className="underline underline-offset-2">
          {t.refunds}
        </Link>
      </p>
    </section>
  );
}

/** «Quero o Pro» na página de preços: abre o plano do espaço pessoal, onde se subscreve. */
export function SubscribePage() {
  const me = useMe();
  return (
    <QueryView q={me}>
      {(m) => <Navigate to={`/w/${m.personalWorkspaceId}/definicoes/plano`} replace />}
    </QueryView>
  );
}
